import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ArrowRight, Loader2, Mic, MicOff, X } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useI18n } from "@/lib/i18n";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";

type Phase = "idle" | "recording" | "processing" | "ready" | "error";

export function VoiceCaptureDialog({
  open,
  onOpenChange,
  onLogged,
  occurredOn,
  onApply,
  workspaceTeamId,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onLogged: () => void;
  /**
   * EDITOR mode. When provided, "log it" hands the classified items back to the
   * caller (to add to a receipt's content) INSTEAD of creating private signals.
   * The pending memo is discarded so nothing is double-recorded.
   */
  onApply?: (result: { highlights: string[]; shipped: string[]; blockers: string[] }) => void;
  /**
   * Target day (YYYY-MM-DD) for the voice-derived signals. When set,
   * the resulting highlights/shipped/blockers are pinned to noon UTC of
   * that day instead of the recording's wall-clock time. Used by the
   * per-day mic icon on each WeekRibbon card so a user can dictate
   * notes for "yesterday" without those notes landing under today.
   * Unset = legacy "occurs now" behaviour for the global mic button.
   */
  occurredOn?: string;
  /**
   * The ACTIVE workspace the capture belongs to. Threaded through to
   * `voice.applyMemo` so voice captures land where the user is looking,
   * exactly like typed capture. Unset (e.g. EDITOR mode, which inserts no
   * signals) → server keeps its primary-team fallback.
   */
  workspaceTeamId?: number | null;
}) {
  const { t, locale } = useI18n();
  const [phase, setPhase] = useState<Phase>("idle");
  const [seconds, setSeconds] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [classified, setClassified] = useState<{ highlights: string[]; shipped: string[]; blockers: string[] }>(
    { highlights: [], shipped: [], blockers: [] },
  );
  const [memoId, setMemoId] = useState<number | null>(null);
  const [errorMsg, setErrorMsg] = useState("");
  // Per-recording language override.
  //   auto       — server applies user prefs + smart probe
  //   bilingual  — force dual-pass (English + Arabic in parallel),
  //                merged segment-by-segment by confidence. The
  //                correct path for code-switched MENA speech
  //                ("we'll do الـ deployment الأسبوع القادم"). ~2x
  //                cost vs. single-pass; accuracy is the only
  //                strategy that actually handles mixed speech.
  //   en / ar    — single forced pass, fastest, only when you know
  //                you're speaking one language
  const [langOverride, setLangOverride] =
    useState<"auto" | "bilingual" | "en" | "ar">("auto");
  const applyMutation = trpc.voice.applyMemo.useMutation();
  const discardMutation = trpc.voice.discardMemo.useMutation();

  const mediaRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Wake Lock keeps the screen awake during recording on mobile.
  // Without this, the device dims/locks after ~30s of no touch and
  // Safari/Chrome may suspend MediaRecorder along with the page,
  // truncating the recording silently. WakeLock API is available on
  // iOS 16.4+ and Android Chrome — `request()` rejects on older
  // browsers but we treat that as a no-op (best-effort).
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  // Accumulator for re-record-without-discard. When the user is in
  // the "ready" phase reviewing classified items and taps the mic
  // again to ADD more, the new recording's items should be appended
  // to what's already there, not wipe it. We stash the existing
  // classification + memoId here while the new recording runs, then
  // merge once the second classification lands.
  const pendingMergeRef = useRef<{
    highlights: string[];
    shipped: string[];
    blockers: string[];
    transcript: string;
    memoIds: number[];
  } | null>(null);

  useEffect(() => {
    if (!open) {
      stopAll(true);
      // If the user closed the dialog with a pending memo (X button, esc),
      // discard it server-side so the row doesn't sit pending forever.
      if (memoId !== null && phase === "ready") {
        discardMutation.mutate({ memoId });
      }
      setPhase("idle");
      setSeconds(0);
      setTranscript("");
      setClassified({ highlights: [], shipped: [], blockers: [] });
      setMemoId(null);
      setErrorMsg("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function stopAll(silent = false) {
    if (mediaRef.current && mediaRef.current.state !== "inactive") {
      try { mediaRef.current.stop(); } catch {}
    }
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    if (mediaRef.current) {
      mediaRef.current.stream.getTracks().forEach(tr => tr.stop());
      mediaRef.current = null;
    }
    // Release the screen wake lock so the device can dim normally
    // after recording ends. Lock survives until we explicitly
    // release — leaking it would keep the screen on indefinitely.
    if (wakeLockRef.current) {
      wakeLockRef.current.release().catch(() => {});
      wakeLockRef.current = null;
    }
    if (silent) chunksRef.current = [];
  }

  async function startRecording() {
    setErrorMsg("");
    // If the user is re-recording from the "ready" review state,
    // stash the existing classification so we can merge after the
    // new pass. Otherwise the old fix would wipe their work.
    if (phase === "ready") {
      pendingMergeRef.current = {
        highlights: classified.highlights,
        shipped: classified.shipped,
        blockers: classified.blockers,
        transcript,
        memoIds: memoId !== null ? [memoId] : [],
      };
      setClassified({ highlights: [], shipped: [], blockers: [] });
      setTranscript("");
      setMemoId(null);
    } else {
      pendingMergeRef.current = null;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType: pickMime() });
      chunksRef.current = [];
      rec.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = onStopRecording;
      rec.start(250);
      mediaRef.current = rec;
      setPhase("recording");
      setSeconds(0);
      tickRef.current = setInterval(() => setSeconds(s => s + 1), 1000);
      // Hold the screen awake. Best-effort: errors are swallowed
      // on browsers without the API or when the page isn't focused.
      try {
        if ("wakeLock" in navigator) {
          wakeLockRef.current = await (navigator as any).wakeLock.request(
            "screen",
          );
        }
      } catch {
        /* WakeLock denied or unsupported — recording continues. */
      }
    } catch (e: any) {
      setErrorMsg(e?.message || "Microphone permission denied");
      setPhase("error");
    }
  }

  function stopRecording() {
    if (mediaRef.current && mediaRef.current.state !== "inactive") {
      mediaRef.current.stop();
    }
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }

  async function onStopRecording() {
    setPhase("processing");
    const blob = new Blob(chunksRef.current, { type: chunksRef.current[0]?.type || "audio/webm" });
    chunksRef.current = [];

    try {
      const form = new FormData();
      form.append("audio", blob, `voice-${Date.now()}.webm`);
      form.append("durationSeconds", String(seconds));
      form.append("locale", locale);
      // `x-language` is the per-recording override; `x-locale` keeps
      // the UI locale (used for misc display fallbacks server-side).
      // When override is "auto" we skip the header so the server
      // applies the user's prefs / smart probe.
      const headers: Record<string, string> = {
        "x-duration": String(seconds),
        "x-locale": locale,
        "content-type": blob.type,
      };
      if (langOverride !== "auto") headers["x-language"] = langOverride;
      const res = await fetch("/api/voice/upload", {
        method: "POST",
        body: blob,
        headers,
      });
      if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      const data = (await res.json()) as {
        memoId: number | null;
        audioKey: string;
        audioUrl: string;
        transcript: string;
        detectedLocale?: string;
        classified: { highlights: string[]; shipped: string[]; blockers: string[] };
      };
      // Merge with a stashed prior recording (set in startRecording
      // when the user tapped mic again from the "ready" review).
      // Dedupe by trimmed lowercase; concat preserves order so the
      // older items stay above the new ones in the review list.
      const prior = pendingMergeRef.current;
      pendingMergeRef.current = null;
      const dedupe = (arr: string[]) => {
        const seen = new Set<string>();
        const out: string[] = [];
        for (const item of arr) {
          const k = item.trim().toLowerCase();
          if (!k || seen.has(k)) continue;
          seen.add(k);
          out.push(item);
        }
        return out;
      };
      if (prior) {
        setTranscript(
          [prior.transcript, data.transcript].filter(Boolean).join("\n\n"),
        );
        setClassified({
          highlights: dedupe([...prior.highlights, ...data.classified.highlights]),
          shipped: dedupe([
            ...prior.shipped,
            ...data.classified.shipped,
          ]),
          blockers: dedupe([
            ...prior.blockers,
            ...data.classified.blockers,
          ]),
        });
        // Server-side: discard the prior memo so it doesn't sit
        // pending forever. The visible classified items have been
        // merged with the new memo's id, and applyMemo only
        // commits one memo at a time, so we keep the newest id.
        for (const oldId of prior.memoIds) {
          discardMutation.mutate({ memoId: oldId });
        }
      } else {
        setTranscript(data.transcript);
        setClassified(data.classified);
      }
      setMemoId(data.memoId);
      setPhase("ready");
      // Note: signals are NOT inserted yet. The user reviews the
      // classification and then taps "Looks right, log it" to commit, or
      // closes the dialog to discard.
    } catch (e: any) {
      setErrorMsg(e?.message || "Voice processing failed");
      setPhase("error");
    } finally {
      if (mediaRef.current) {
        mediaRef.current.stream.getTracks().forEach(tr => tr.stop());
        mediaRef.current = null;
      }
    }
  }

  function pickMime() {
    const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
    for (const c of candidates) {
      if ((window as any).MediaRecorder?.isTypeSupported?.(c)) return c;
    }
    return "audio/webm";
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // Custom close button instead of the default (which has a
        // light background that clashes with this dialog's dark
        // theme and positioned awkwardly with `float-end`).
        showCloseButton={false}
        className="
          inset-0 left-0 top-0 translate-x-0 translate-y-0
          w-screen h-[100dvh] max-w-none rounded-none
          sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2
          sm:w-auto sm:h-auto sm:max-w-md sm:max-h-[90dvh] sm:rounded-2xl
          bg-[oklch(0.18_0.01_50)] text-white border-0
          p-0 overflow-hidden
        "
      >
        {/* Radix requires a DialogTitle for screen readers. The voice capture
            UI is purely a visual orb + transcript, so the title is visually
            hidden via Tailwind's `sr-only` utility but still announced. */}
        <DialogTitle className="sr-only">
          {t.voiceCapture ?? (locale === "ar" ? "التقاط صوتي" : "Voice capture")}
        </DialogTitle>

        {/* Themed close button — positioned at the top-end corner
            with dark styling so it reads on the dark backdrop. */}
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          aria-label={locale === "ar" ? "إغلاق" : "Close"}
          className="absolute top-[calc(1rem+env(safe-area-inset-top))] end-4 z-10 h-9 w-9 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white/80 hover:text-white transition-colors"
        >
          <X className="h-4 w-4" />
        </button>

        {/*
          Vertical centring on full-screen mobile: an outer column
          that fills 100dvh, an inner column that grows to take
          available space (`flex-1`) and centres its children. On
          desktop the dialog is auto-sized so we just stack normally.
          Padding lives inside so the close button + mic don't get
          clipped at the edges.
        */}
        <div className="flex flex-col h-full sm:h-auto sm:max-h-[90dvh] px-6 py-10 sm:py-8 overflow-y-auto">
          <div className="flex-1 sm:flex-none flex flex-col items-center justify-center text-center gap-6">
          <div className="text-xs text-white/60">
            {new Date().toLocaleString(locale === "ar" ? "ar" : "en", {
              weekday: "long", month: "short", day: "numeric",
              hour: "numeric", minute: "2-digit",
            })}
          </div>

          <button
            type="button"
            disabled={phase === "processing"}
            onClick={() => phase === "recording" ? stopRecording() : startRecording()}
            className="relative h-44 w-44 rounded-full focus:outline-none"
            aria-label={t.tapToSpeak}
          >
            {phase === "recording" && (
              <>
                <span className="absolute inset-0 rounded-full bg-coral/30 animate-ping" />
                <span className="absolute -inset-3 rounded-full bg-coral/15 animate-pulse" />
              </>
            )}
            <span
              className={
                "absolute inset-0 rounded-full grid place-items-center " +
                "bg-gradient-to-br from-coral to-amber-500 shadow-[0_30px_60px_-20px_rgba(232,121,100,0.7)]"
              }
            >
              {phase === "processing"
                ? <Loader2 className="h-12 w-12 text-white animate-spin" />
                : phase === "recording"
                ? <MicOff className="h-12 w-12 text-white" />
                : <Mic className="h-12 w-12 text-white" />}
            </span>
          </button>

          {/* Language strategy picker. "Mixed" is the right answer
              for most MENA users who code-switch — it runs two
              passes (en + ar) in parallel and merges by per-segment
              confidence, so each phrase ends up transcribed by the
              model that's confident about it. Costs ~2x vs single-
              pass but is the only strategy that doesn't butcher
              code-switching. "Auto" keeps the smart probe; "English"
              / "Arabic" force a single language. */}
          {phase === "idle" && (
            <div className="inline-flex rounded-lg bg-white/10 p-1 mb-4 flex-wrap gap-1">
              {(
                [
                  { v: "auto", en: "Auto", ar: "تلقائي" },
                  { v: "bilingual", en: "Mixed", ar: "مختلط" },
                  { v: "en", en: "English", ar: "إنجليزي" },
                  { v: "ar", en: "Arabic", ar: "عربي" },
                ] as const
              ).map(opt => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setLangOverride(opt.v)}
                  className={
                    "px-3 py-1 text-xs rounded-md transition-colors " +
                    (langOverride === opt.v
                      ? "bg-white/20 text-white"
                      : "text-white/60 hover:text-white")
                  }
                >
                  {locale === "ar" ? opt.ar : opt.en}
                </button>
              ))}
            </div>
          )}
          {phase === "idle" && langOverride === "bilingual" && (
            <div className="text-[11px] text-white/50 mb-3 max-w-[260px]">
              {locale === "ar"
                ? "يشغّل ممرّي تفريغ بالعربية والإنجليزية ويدمج النتيجة. الأفضل عند خلط اللغتين، لكن أبطأ قليلاً."
                : "Runs Arabic + English passes in parallel and merges by segment confidence. Best for code-switched speech, slightly slower."}
            </div>
          )}

          <div className="min-h-[80px] w-full">
            {phase === "idle" && (
              <div className="text-white/70 text-base">{t.tapToSpeak}</div>
            )}
            {phase === "recording" && (
              <div className="text-white text-base">
                {t.listening} <span className="font-mono ms-2">{formatTime(seconds)}</span>
              </div>
            )}
            {phase === "processing" && (
              <div className="text-white/70 text-base">…</div>
            )}
            {phase === "ready" && (
              <div className="text-white text-start space-y-3">
                {/* Editable raw transcript. Same intent as the photo
                    dialog's extracted-text textarea: fix mishears
                    before commit. The transcript itself isn't a
                    signal (it's archived on the voiceMemo row); only
                    the classified chips below become signals. */}
                <textarea
                  value={transcript}
                  onChange={e => setTranscript(e.target.value)}
                  rows={Math.min(6, Math.max(2, transcript.split("\n").length))}
                  placeholder={locale === "ar" ? "النص…" : "Transcript…"}
                  className="w-full leading-snug bg-white/5 hover:bg-white/10 focus:bg-white/10 rounded-lg p-2 border border-white/15 focus:border-white/40 outline-none resize-y text-white placeholder-white/40"
                />
                <div className="space-y-1.5">
                  {classified.highlights.map((s, i) => (
                    <RemovableChip
                      key={"t" + i}
                      color="emerald"
                      prefix={locale === "ar" ? "إنجاز: " : "Highlight: "}
                      value={s}
                      onChange={next =>
                        setClassified(c => {
                          const highlights = c.highlights.slice();
                          highlights[i] = next;
                          return { ...c, highlights };
                        })
                      }
                      onRemove={() =>
                        setClassified(c => ({
                          ...c,
                          highlights: c.highlights.filter((_, j) => j !== i),
                        }))
                      }
                      onMoveTo={target =>
                        setClassified(c =>
                          moveVoiceItem(c, "highlights", target, i),
                        )
                      }
                      category="highlights"
                    />
                  ))}
                  {classified.shipped.map((s, i) => (
                    <RemovableChip
                      key={"m" + i}
                      color="sky"
                      prefix={locale === "ar" ? "أُنجز: " : "Shipped: "}
                      value={s}
                      onChange={next =>
                        setClassified(c => {
                          const shipped = c.shipped.slice();
                          shipped[i] = next;
                          return { ...c, shipped };
                        })
                      }
                      onRemove={() =>
                        setClassified(c => ({
                          ...c,
                          shipped: c.shipped.filter((_, j) => j !== i),
                        }))
                      }
                      onMoveTo={target =>
                        setClassified(c =>
                          moveVoiceItem(c, "shipped", target, i),
                        )
                      }
                      category="shipped"
                    />
                  ))}
                  {classified.blockers.map((s, i) => (
                    <RemovableChip
                      key={"b" + i}
                      color="rose"
                      prefix={locale === "ar" ? "عائق: " : "Blocker: "}
                      value={s}
                      onChange={next =>
                        setClassified(c => {
                          const blockers = c.blockers.slice();
                          blockers[i] = next;
                          return { ...c, blockers };
                        })
                      }
                      onRemove={() =>
                        setClassified(c => ({
                          ...c,
                          blockers: c.blockers.filter((_, j) => j !== i),
                        }))
                      }
                      onMoveTo={target =>
                        setClassified(c =>
                          moveVoiceItem(c, "blockers", target, i),
                        )
                      }
                      category="blockers"
                    />
                  ))}
                  {classified.highlights.length +
                    classified.shipped.length +
                    classified.blockers.length === 0 && (
                    <div className="text-xs text-white/60 italic">
                      {locale === "ar"
                        ? "لم يُصنَّف أي عنصر. احفظه كمهمّة بالأسفل أو أغلق للتجاهل."
                        : "Nothing auto-classified. Use “Save as task” below, or close to discard."}
                    </div>
                  )}
                </div>
              </div>
            )}
            {phase === "error" && (
              <div className="text-rose-300 text-sm">{errorMsg}</div>
            )}
          </div>

          {phase === "ready" &&
            classified.highlights.length +
              classified.shipped.length +
              classified.blockers.length ===
              0 &&
            transcript.trim().length > 0 && (
              // Fallback: when the LLM returned no chips but Whisper got real
              // text, let the user save the raw transcript as a single task
              // instead of forcing them to start over. This is the most
              // common voice-capture failure mode and the empty-state used
              // to be a dead end.
              <Button
                disabled={memoId === null || applyMutation.isPending}
                onClick={async () => {
                  if (memoId === null) return;
                  try {
                    const r = await applyMutation.mutateAsync({
                      memoId,
                      highlights: [transcript.trim().slice(0, 200)],
                      shipped: [],
                      blockers: [],
                      ...(occurredOn ? { occurredOn } : {}),
                      workspaceTeamId,
                    });
                    if (r.inserted > 0) {
                      onLogged();
                      toast.success(
                        locale === "ar"
                          ? "أُضيفت كمهمّة"
                          : "Saved as a task",
                      );
                    }
                    setMemoId(null);
                    onOpenChange(false);
                  } catch (e: any) {
                    toast.error(e?.message || "Couldn't save memo");
                  }
                }}
                className="w-full bg-white/10 hover:bg-white/20 text-white border border-white/20"
              >
                {applyMutation.isPending && (
                  <Loader2 className="me-2 h-4 w-4 animate-spin" />
                )}
                {locale === "ar" ? "احفظ كمهمّة" : "Save as task"}
              </Button>
            )}

          {phase === "ready" && (
            <Button
              disabled={memoId === null || applyMutation.isPending}
              onClick={async () => {
                if (memoId === null) return;
                // Trim + drop empties so an edited-blank chip can't
                // insert a blank item.
                const clean = (xs: string[]) =>
                  xs.map(s => s.trim()).filter(s => s.length > 0);
                // EDITOR mode: hand the classified items to the caller and
                // discard the pending memo (no signal is created).
                if (onApply) {
                  onApply({
                    highlights: clean(classified.highlights),
                    shipped: clean(classified.shipped),
                    blockers: clean(classified.blockers),
                  });
                  // Leave memoId set so the close-handler discards the memo.
                  onOpenChange(false);
                  return;
                }
                try {
                  const r = await applyMutation.mutateAsync({
                    memoId,
                    highlights: clean(classified.highlights),
                    shipped: clean(classified.shipped),
                    blockers: clean(classified.blockers),
                    ...(occurredOn ? { occurredOn } : {}),
                    workspaceTeamId,
                  });
                  if (r.inserted > 0) {
                    onLogged();
                    toast.success(
                      locale === "ar"
                        ? `أُضيف ${r.inserted} عنصراً`
                        : `Logged ${r.inserted} item${r.inserted === 1 ? "" : "s"}`,
                    );
                  } else {
                    toast(locale === "ar" ? "لم يُضَف شيء" : "Nothing logged");
                  }
                  setMemoId(null); // prevent the close-handler from discarding
                  onOpenChange(false);
                } catch (e: any) {
                  toast.error(e?.message || "Couldn't apply memo");
                }
              }}
              className="w-full bg-gradient-to-r from-coral to-amber-500 hover:opacity-95 text-white"
            >
              {applyMutation.isPending && (
                <Loader2 className="me-2 h-4 w-4 animate-spin" />
              )}
              {t.looksRight}
            </Button>
          )}

          {phase === "ready" && (
            <div className="text-xs text-white/50">{t.spokeForN(seconds)}</div>
          )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Editable chip for the voice review state. Mis-classification or a
 * Whisper slip ("clik" → "Qlik") used to require discard-and-redo;
 * inline editing here lets the user fix what they hear is wrong and
 * commit in the same dialog. The prefix label ("Highlight: ", "Shipped: ",
 * "Blocker: ") is rendered as a static prefix so the user only edits
 * the actual item content.
 */
type VoiceCategory = "highlights" | "shipped" | "blockers";

/**
 * Move an item between the three classified arrays.
 * Pure functional — returns the new shape so React.setState can
 * batch the source delete + target append into one render.
 */
function moveVoiceItem(
  c: { highlights: string[]; shipped: string[]; blockers: string[] },
  from: VoiceCategory,
  to: VoiceCategory,
  idx: number,
): { highlights: string[]; shipped: string[]; blockers: string[] } {
  if (from === to) return c;
  const next = {
    highlights: c.highlights.slice(),
    shipped: c.shipped.slice(),
    blockers: c.blockers.slice(),
  };
  const item = next[from][idx];
  if (item === undefined) return c;
  next[from] = next[from].filter((_, i) => i !== idx);
  next[to] = [...next[to], item];
  return next;
}

function RemovableChip({
  color,
  prefix,
  value,
  onChange,
  onRemove,
  onMoveTo,
  category,
}: {
  color: "emerald" | "sky" | "rose";
  prefix: string;
  value: string;
  onChange: (next: string) => void;
  onRemove: () => void;
  /** Reclassify this chip into a different category. */
  onMoveTo?: (target: VoiceCategory) => void;
  category?: VoiceCategory;
}) {
  const styles =
    color === "emerald"
      ? "border-emerald-400/40 text-emerald-200 focus-within:border-emerald-300"
      : color === "sky"
        ? "border-sky-400/40 text-sky-200 focus-within:border-sky-300"
        : "border-rose-400/40 text-rose-200 focus-within:border-rose-300";
  return (
    <div
      className={
        "rounded-2xl border ps-3 pe-1 py-1.5 text-sm flex items-start gap-1 " +
        styles
      }
    >
      <span className="shrink-0 opacity-80 font-medium">{prefix}</span>
      <textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        rows={1}
        className="flex-1 min-w-0 bg-transparent resize-none outline-none text-sm leading-snug break-words text-white placeholder-white/40"
        onInput={e => {
          const el = e.currentTarget;
          el.style.height = "auto";
          el.style.height = el.scrollHeight + "px";
        }}
      />
      {onMoveTo && category && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Reclassify"
              title="Move to…"
              className="shrink-0 rounded-full p-1 hover:bg-white/10 transition-colors"
            >
              <ArrowRight className="h-3 w-3" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[7rem]">
            {(["highlights", "shipped", "blockers"] as VoiceCategory[])
              .filter(c => c !== category)
              .map(c => (
                <DropdownMenuItem
                  key={c}
                  onSelect={() => onMoveTo(c)}
                >
                  Move to {c === "highlights" ? "Highlight" : c === "shipped" ? "Shipped" : "Blocker"}
                </DropdownMenuItem>
              ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove"
        className="shrink-0 rounded-full p-1 hover:bg-white/10 transition-colors"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}

function formatTime(s: number) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
}
